import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  adminCantonsAPI,
  adminTeamProvisioningAPI,
  adminSeasonsAPI,
  adminEventTournamentsAPI,
} from '../services/adminApi';

const Wrap = styled.div`
  padding: 1.5rem 2rem;
  max-width: 980px;
`;
const Intro = styled.p`
  color: #4b5563;
  font-size: 0.95rem;
  line-height: 1.55;
  margin-bottom: 1.25rem;
`;
const TabRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;
const TabBtn = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 6px;
  border: 1px solid ${(p) => (p.$on ? '#043663' : '#d1d5db')};
  background: ${(p) => (p.$on ? '#043663' : '#fff')};
  color: ${(p) => (p.$on ? '#fff' : '#374151')};
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
`;
const Card = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 1.1rem 1.2rem;
  margin-bottom: 1.25rem;
  background: #fff;
`;
const PageTitle = styled.h2`
  font-size: 1.25rem;
  margin: 0 0 0.5rem 0;
  color: #111827;
`;
const CardTitle = styled.h3`
  margin: 0 0 0.75rem 0;
  font-size: 1rem;
  color: #111827;
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;
const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
`;
const Input = styled.input`
  padding: 0.5rem 0.65rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
`;
const Select = styled.select`
  padding: 0.5rem 0.65rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
`;
const BtnPrimary = styled.button`
  padding: 0.55rem 1.1rem;
  background: #043663;
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  margin-top: 0.75rem;
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;
const Hint = styled.p`
  font-size: 0.8rem;
  color: #6b7280;
  margin: 0 0 0.75rem 0;
  line-height: 1.45;
`;
const SearchBox = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 0.65rem;
`;
const PickList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  max-height: 11rem;
  overflow: auto;
`;
const PickItem = styled.li`
  padding: 0.45rem 0.65rem;
  font-size: 0.8125rem;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
  background: ${(p) => (p.$active ? '#043663' : 'transparent')};
  color: ${(p) => (p.$active ? '#fff' : 'inherit')};
  &:hover {
    background: ${(p) => (p.$active ? '#052d4f' : '#f9fafb')};
  }
`;

function unwrap(res) {
  if (res && typeof res === 'object' && 'data' in res && res.success !== false) return res.data;
  return res;
}

const AdminTeamProvisioningPanel = () => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('wizard'); // wizard | split | season

  const [wizard, setWizard] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    skill_level: '4',
    team_name: '',
    canton: '',
    team_skill_level: '4',
    home_club: '',
  });

  const [soloPlayer, setSoloPlayer] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    skill_level: '4',
    home_club: '',
  });

  const [shell, setShell] = useState({
    team_name: '',
    canton: '',
    team_skill_level: '4',
    season_id: '',
  });

  const [linkTeamId, setLinkTeamId] = useState('');
  const [linkTeamQuery, setLinkTeamQuery] = useState('');
  const [linkPlayerId, setLinkPlayerId] = useState('');
  const [linkSearch, setLinkSearch] = useState('');
  const [linkPosition, setLinkPosition] = useState('captain');

  const [seasonRoster, setSeasonRoster] = useState({
    campaignId: '',
    seasonId: '',
    team_name: '',
    canton: '',
    team_skill_level: '4',
    captain: {
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      skill_level: '4',
    },
    partner: {
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      skill_level: '4',
    },
  });
  const [seasonRosterResult, setSeasonRosterResult] = useState(null);

  const { data: cantonsRes } = useQuery(['adminCantonsProvisioning'], () => adminCantonsAPI.getList(), {
    retry: false,
  });
  const cantonsRaw = unwrap(cantonsRes);
  const cantons = Array.isArray(cantonsRaw) ? cantonsRaw : [];

  const { data: campaignsRes } = useQuery(
    ['adminCampaignsProvisioning'],
    () => adminEventTournamentsAPI.getAll(),
    { retry: false, staleTime: 60 * 1000 }
  );
  const campaignsRaw = unwrap(campaignsRes);
  const campaigns = Array.isArray(campaignsRaw)
    ? campaignsRaw
    : Array.isArray(campaignsRaw?.tournaments)
      ? campaignsRaw.tournaments
      : Array.isArray(campaignsRaw?.items)
        ? campaignsRaw.items
        : [];

  const { data: linkedSeasonsRes } = useQuery(
    ['adminCampaignSeasonsProvisioning', seasonRoster.campaignId],
    () => adminEventTournamentsAPI.getLinkedSeasons(seasonRoster.campaignId),
    { enabled: !!seasonRoster.campaignId, retry: false }
  );
  const linkedSeasonsRaw = unwrap(linkedSeasonsRes);
  const linkedSeasons = Array.isArray(linkedSeasonsRaw)
    ? linkedSeasonsRaw
    : Array.isArray(linkedSeasonsRaw?.seasons)
      ? linkedSeasonsRaw.seasons
      : [];

  const { data: allSeasonsRes } = useQuery(
    ['adminSeasonsProvisioning'],
    () => adminSeasonsAPI.getAll(),
    { retry: false, staleTime: 60 * 1000 }
  );
  const allSeasonsRaw = unwrap(allSeasonsRes);
  const allSeasons = Array.isArray(allSeasonsRaw)
    ? allSeasonsRaw
    : Array.isArray(allSeasonsRaw?.seasons)
      ? allSeasonsRaw.seasons
      : Array.isArray(allSeasonsRaw?.data)
        ? allSeasonsRaw.data
        : [];

  const seasonOptions =
    seasonRoster.campaignId && linkedSeasons.length > 0 ? linkedSeasons : allSeasons;

  useEffect(() => {
    if (shell.season_id || !allSeasons.length) return;
    const live =
      allSeasons.find((s) => String(s.status || '').toLowerCase() === 'live') ||
      allSeasons.find((s) => String(s.status || '').toLowerCase() === 'active') ||
      allSeasons[0];
    const sid = live?.id || live?.season_id;
    if (sid) setShell((s) => ({ ...s, season_id: sid }));
  }, [allSeasons, shell.season_id]);

  const { data: teamsRes } = useQuery(
    ['adminTeamsProvisioningShell'],
    () => adminTeamProvisioningAPI.searchTeams({ q: '', limit: 5000 }),
    { retry: false, staleTime: 30 * 1000 }
  );
  const teamsRows = Array.isArray(unwrap(teamsRes)) ? unwrap(teamsRes) : [];
  const teamNeedle = linkTeamQuery.trim().toLowerCase();
  const filteredTeams = !teamNeedle
    ? teamsRows
    : teamsRows.filter((t) => String(t.team_name || '').toLowerCase().includes(teamNeedle));
  const selectedTeam = teamsRows.find((t) => t.id === linkTeamId) || null;

  const { data: searchRes, refetch: refetchSearch } = useQuery(
    ['provisionPlayerSearch', linkSearch],
    () => adminTeamProvisioningAPI.searchPlayers({ q: linkSearch.trim() || ' ', limit: 30 }),
    { enabled: false, retry: false }
  );

  const searchHits = Array.isArray(unwrap(searchRes)) ? unwrap(searchRes) : [];

  const invalidateCaches = () => {
    queryClient.invalidateQueries(['adminTeams']);
    queryClient.invalidateQueries(['adminTeamsProvisioningShell']);
    queryClient.invalidateQueries('admin-players');
  };

  const wizardMutation = useMutation(() =>
    adminTeamProvisioningAPI.playerThenTeamCaptain({
      player: {
        email: wizard.email.trim(),
        password: wizard.password,
        first_name: wizard.first_name.trim(),
        last_name: wizard.last_name.trim(),
        phone_number: wizard.phone_number.trim() || undefined,
        skill_level: wizard.skill_level ? Number(wizard.skill_level) : undefined,
        home_club: wizard.home_club.trim() || undefined,
        must_change_password: true,
      },
      team_shell: {
        team_name: wizard.team_name.trim(),
        canton: wizard.canton,
        team_skill_level: wizard.team_skill_level ? Number(wizard.team_skill_level) : undefined,
      },
    }), {
      onSuccess: (res) => {
        const d = unwrap(res);
        toast.success(
          `Created player ${d?.player?.email || ''}, team "${d?.team?.team_name || ''}" (${d?.team?.id?.slice(0, 8)}…) as captain.`
        );
        invalidateCaches();
      },
      onError: (e) =>
        toast.error(e.response?.data?.message || e.message || 'Wizard failed'),
    });

  const createPlayerMutation = useMutation(
    () =>
      adminTeamProvisioningAPI.createPlayer({
        ...soloPlayer,
        email: soloPlayer.email.trim(),
        first_name: soloPlayer.first_name.trim(),
        last_name: soloPlayer.last_name.trim(),
        phone_number: soloPlayer.phone_number.trim() || undefined,
        skill_level: soloPlayer.skill_level ? Number(soloPlayer.skill_level) : undefined,
        must_change_password: true,
      }),
    {
      onSuccess: () => {
        toast.success('Player created. They can log in with player login using this email.');
        invalidateCaches();
      },
      onError: (e) =>
        toast.error(e.response?.data?.message || e.message || 'Create player failed'),
    }
  );

  const shellMutation = useMutation(
    () =>
      adminTeamProvisioningAPI.createTeamShell({
        team_name: shell.team_name.trim(),
        canton: shell.canton,
        team_skill_level: shell.team_skill_level ? Number(shell.team_skill_level) : undefined,
        season_id: shell.season_id || undefined,
      }),
    {
      onSuccess: (res) => {
        const d = unwrap(res);
        const phaseName = d?.season?.name;
        toast.success(
          phaseName
            ? `Team shell "${d?.team?.team_name}" is in ${phaseName}. Find it in Teams, then assign a division.`
            : `Team shell "${d?.team?.team_name}" created. Select a phase next time so it appears in Teams.`
        );
        invalidateCaches();
      },
      onError: (e) =>
        toast.error(e.response?.data?.message || e.message || 'Create team failed'),
    }
  );

  const linkMutation = useMutation(
    () =>
      adminTeamProvisioningAPI.linkPlayerToTeam({
        teamId: linkTeamId,
        playerId: linkPlayerId,
        position: linkPosition,
      }),
    {
      onSuccess: () => {
        toast.success('Player linked on roster.');
        invalidateCaches();
      },
      onError: (e) =>
        toast.error(e.response?.data?.message || e.message || 'Link failed'),
    }
  );

  const seasonRosterReady =
    !!seasonRoster.seasonId &&
    !!seasonRoster.team_name.trim() &&
    !!seasonRoster.canton &&
    !!seasonRoster.captain.email.trim() &&
    seasonRoster.captain.password.length >= 8 &&
    !!seasonRoster.captain.first_name.trim() &&
    !!seasonRoster.captain.last_name.trim() &&
    !!seasonRoster.partner.email.trim() &&
    seasonRoster.partner.password.length >= 8 &&
    !!seasonRoster.partner.first_name.trim() &&
    !!seasonRoster.partner.last_name.trim();

  const seasonRosterMutation = useMutation(
    () =>
      adminTeamProvisioningAPI.createSeasonRoster({
        campaignId: seasonRoster.campaignId || undefined,
        seasonId: seasonRoster.seasonId,
        team: {
          team_name: seasonRoster.team_name.trim(),
          canton: seasonRoster.canton,
          team_skill_level: seasonRoster.team_skill_level
            ? Number(seasonRoster.team_skill_level)
            : undefined,
        },
        playerCaptain: {
          email: seasonRoster.captain.email.trim(),
          password: seasonRoster.captain.password,
          first_name: seasonRoster.captain.first_name.trim(),
          last_name: seasonRoster.captain.last_name.trim(),
          skill_level: seasonRoster.captain.skill_level
            ? Number(seasonRoster.captain.skill_level)
            : undefined,
          must_change_password: true,
        },
        playerPartner: {
          email: seasonRoster.partner.email.trim(),
          password: seasonRoster.partner.password,
          first_name: seasonRoster.partner.first_name.trim(),
          last_name: seasonRoster.partner.last_name.trim(),
          skill_level: seasonRoster.partner.skill_level
            ? Number(seasonRoster.partner.skill_level)
            : undefined,
          must_change_password: true,
        },
      }),
    {
      onSuccess: (res) => {
        const d = unwrap(res);
        setSeasonRosterResult(d);
        toast.success(
          `Phase roster ready: "${d?.team?.team_name || ''}" in ${d?.season?.name || 'phase'} with 2 players.`
        );
        invalidateCaches();
      },
      onError: (e) =>
        toast.error(e.response?.data?.message || e.message || 'Phase roster failed'),
    }
  );

  return (
    <Wrap>
      <PageTitle>Team & player setup</PageTitle>
      <Intro>
        Create <strong>player</strong> accounts (country tenant inferred from your league tenant) and{' '}
        <strong>team shells</strong> without going through Stripe. Use <strong>Phase roster</strong> when you also need
        a paid phase registration (season + phase) and a full 2-player roster in one step — ideal for staging tests.
        Division placement is still done in Teams / League Management.
      </Intro>

      <TabRow>
        <TabBtn type="button" $on={tab === 'wizard'} onClick={() => setTab('wizard')}>
          Wizard (new player → team → captain)
        </TabBtn>
        <TabBtn type="button" $on={tab === 'split'} onClick={() => setTab('split')}>
          Step by step / link existing
        </TabBtn>
        <TabBtn type="button" $on={tab === 'season'} onClick={() => setTab('season')}>
          Phase roster (season + phase)
        </TabBtn>
      </TabRow>

      {tab === 'wizard' && (
        <Card>
          <CardTitle>One-shot: player + empty team + captain roster</CardTitle>
          <Hint>For a brand-new pair where neither side exists yet.</Hint>
          <Grid>
            <Label>
              Player email *
              <Input
                type="email"
                value={wizard.email}
                onChange={(e) => setWizard((s) => ({ ...s, email: e.target.value }))}
                autoComplete="off"
              />
            </Label>
            <Label>
              Temp password *
              <Input
                type="password"
                value={wizard.password}
                onChange={(e) => setWizard((s) => ({ ...s, password: e.target.value }))}
                autoComplete="new-password"
              />
            </Label>
            <Label>
              First name *
              <Input
                value={wizard.first_name}
                onChange={(e) => setWizard((s) => ({ ...s, first_name: e.target.value }))}
              />
            </Label>
            <Label>
              Last name *
              <Input
                value={wizard.last_name}
                onChange={(e) => setWizard((s) => ({ ...s, last_name: e.target.value }))}
              />
            </Label>
            <Label>
              Phone
              <Input
                value={wizard.phone_number}
                onChange={(e) => setWizard((s) => ({ ...s, phone_number: e.target.value }))}
              />
            </Label>
            <Label>
              Player skill (snapshot)
              <Input
                value={wizard.skill_level}
                onChange={(e) => setWizard((s) => ({ ...s, skill_level: e.target.value }))}
              />
            </Label>
            <Label style={{ gridColumn: 'span 2' }}>
              Home club (optional)
              <Input
                value={wizard.home_club}
                onChange={(e) => setWizard((s) => ({ ...s, home_club: e.target.value }))}
              />
            </Label>
            <Label>
              Team display name *
              <Input
                value={wizard.team_name}
                onChange={(e) => setWizard((s) => ({ ...s, team_name: e.target.value }))}
              />
            </Label>
            <Label>
              Canton *
              <Select value={wizard.canton} onChange={(e) => setWizard((s) => ({ ...s, canton: e.target.value }))}>
                <option value="">—</option>
                {cantons.map((c) => (
                  <option key={c.id || c.code} value={c.code}>
                    {c.name || c.code} ({c.code})
                  </option>
                ))}
              </Select>
            </Label>
            <Label>
              Team skill average (optional)
              <Input
                value={wizard.team_skill_level}
                onChange={(e) => setWizard((s) => ({ ...s, team_skill_level: e.target.value }))}
              />
            </Label>
          </Grid>
          <BtnPrimary
            type="button"
            disabled={
              wizardMutation.isLoading ||
              !wizard.email ||
              wizard.password.length < 8 ||
              !wizard.first_name ||
              !wizard.last_name ||
              !wizard.team_name ||
              !wizard.canton
            }
            onClick={() => wizardMutation.mutate()}
          >
            {wizardMutation.isLoading ? 'Creating…' : 'Run wizard'}
          </BtnPrimary>
        </Card>
      )}

      {tab === 'split' && (
        <>
          <Card>
            <CardTitle>1. Create player only</CardTitle>
            <Hint>Produces a standalone player login (tenant = country backing this league).</Hint>
            <Grid>
              <Label>
                Email *
                <Input
                  type="email"
                  value={soloPlayer.email}
                  onChange={(e) => setSoloPlayer((s) => ({ ...s, email: e.target.value }))}
                />
              </Label>
              <Label>
                Password *
                <Input
                  type="password"
                  value={soloPlayer.password}
                  onChange={(e) => setSoloPlayer((s) => ({ ...s, password: e.target.value }))}
                />
              </Label>
              <Label>
                First name *
                <Input
                  value={soloPlayer.first_name}
                  onChange={(e) => setSoloPlayer((s) => ({ ...s, first_name: e.target.value }))}
                />
              </Label>
              <Label>
                Last name *
                <Input
                  value={soloPlayer.last_name}
                  onChange={(e) => setSoloPlayer((s) => ({ ...s, last_name: e.target.value }))}
                />
              </Label>
              <Label>
                Phone
                <Input
                  value={soloPlayer.phone_number}
                  onChange={(e) => setSoloPlayer((s) => ({ ...s, phone_number: e.target.value }))}
                />
              </Label>
              <Label>
                Skill snapshot
                <Input
                  value={soloPlayer.skill_level}
                  onChange={(e) => setSoloPlayer((s) => ({ ...s, skill_level: e.target.value }))}
                />
              </Label>
              <Label style={{ gridColumn: 'span 2' }}>
                Home club
                <Input
                  value={soloPlayer.home_club}
                  onChange={(e) => setSoloPlayer((s) => ({ ...s, home_club: e.target.value }))}
                />
              </Label>
            </Grid>
            <BtnPrimary
              type="button"
              disabled={
                createPlayerMutation.isLoading ||
                !soloPlayer.email ||
                soloPlayer.password.length < 8 ||
                !soloPlayer.first_name ||
                !soloPlayer.last_name
              }
              onClick={() => createPlayerMutation.mutate()}
            >
              {createPlayerMutation.isLoading ? 'Saving…' : 'Create player'}
            </BtnPrimary>
          </Card>

          <Card>
            <CardTitle>2. Create team shell (no Stripe)</CardTitle>
            <Hint>
              Empty placeholder for a division (e.g. Open slot Basel). Pick the phase so the team appears in Teams —
              then assign the division there. Link real players later.
            </Hint>
            <Grid>
              <Label>
                Team name *
                <Input
                  value={shell.team_name}
                  onChange={(e) => setShell((s) => ({ ...s, team_name: e.target.value }))}
                />
              </Label>
              <Label>
                Canton *
                <Select value={shell.canton} onChange={(e) => setShell((s) => ({ ...s, canton: e.target.value }))}>
                  <option value="">—</option>
                  {cantons.map((c) => (
                    <option key={c.id || c.code} value={c.code}>
                      {c.name || c.code} ({c.code})
                    </option>
                  ))}
                </Select>
              </Label>
              <Label>
                Phase *
                <Select
                  value={shell.season_id}
                  onChange={(e) => setShell((s) => ({ ...s, season_id: e.target.value }))}
                >
                  <option value="">—</option>
                  {allSeasons.map((s) => {
                    const sid = s.id || s.season_id;
                    return (
                      <option key={sid} value={sid}>
                        {s.name || sid}
                      </option>
                    );
                  })}
                </Select>
              </Label>
              <Label>
                Team skill avg
                <Input
                  value={shell.team_skill_level}
                  onChange={(e) => setShell((s) => ({ ...s, team_skill_level: e.target.value }))}
                />
              </Label>
            </Grid>
            <BtnPrimary
              type="button"
              disabled={shellMutation.isLoading || !shell.team_name || !shell.canton || !shell.season_id}
              onClick={() => shellMutation.mutate()}
            >
              {shellMutation.isLoading ? 'Creating…' : 'Create team shell'}
            </BtnPrimary>
          </Card>

          <Card>
            <CardTitle>3. Link player ↔ team roster</CardTitle>
            <Hint>Picks roster slot (captain replaces any prior captain). Max two active roster rows per team.</Hint>
            <Label style={{ marginBottom: '0.4rem' }}>
              Team
              {teamsRows.length ? (
                <span style={{ fontWeight: 500, color: '#6b7280' }}>
                  {filteredTeams.length === teamsRows.length
                    ? `${teamsRows.length} loaded`
                    : `${filteredTeams.length} of ${teamsRows.length}`}
                </span>
              ) : null}
            </Label>
            <SearchBox>
              <Input
                placeholder="Search all teams by name"
                value={linkTeamQuery}
                onChange={(e) => setLinkTeamQuery(e.target.value)}
                style={{ flex: '1', minWidth: '200px' }}
              />
            </SearchBox>
            {selectedTeam && (
              <p style={{ fontSize: '0.82rem', color: '#065f46', margin: '0 0 0.5rem' }}>
                Selected: {selectedTeam.team_name}
                {selectedTeam.canton ? ` · ${selectedTeam.canton}` : ''} ({String(selectedTeam.id).slice(0, 8)}…)
              </p>
            )}
            <PickList style={{ marginBottom: '0.75rem', maxHeight: '16rem' }}>
              {filteredTeams.map((t) => (
                <PickItem key={t.id} $active={t.id === linkTeamId} onClick={() => setLinkTeamId(t.id)}>
                  {t.team_name}
                  {t.canton ? ` · ${t.canton}` : ''} ({String(t.id).slice(0, 8)}…)
                </PickItem>
              ))}
              {!filteredTeams.length && (
                <PickItem style={{ cursor: 'default', color: '#9ca3af' }}>
                  {teamsRows.length ? 'No teams match that search' : 'Loading teams…'}
                </PickItem>
              )}
            </PickList>
            <Label style={{ marginBottom: '0.65rem' }}>
              Role
              <Select value={linkPosition} onChange={(e) => setLinkPosition(e.target.value)}>
                <option value="captain">Captain</option>
                <option value="member">Member</option>
              </Select>
            </Label>
            <SearchBox>
              <Input
                placeholder="Search player by email or name"
                value={linkSearch}
                onChange={(e) => setLinkSearch(e.target.value)}
                style={{ flex: '1', minWidth: '200px' }}
              />
              <BtnPrimary type="button" style={{ marginTop: 0 }} onClick={() => refetchSearch()}>
                Search
              </BtnPrimary>
            </SearchBox>
            {linkPlayerId && (
              <p style={{ fontSize: '0.82rem', color: '#065f46', marginBottom: '0.5rem' }}>
                Selected player id: {linkPlayerId.slice(0, 8)}…
              </p>
            )}
            <PickList>
              {Array.isArray(searchHits) &&
                searchHits.map((p) => (
                  <PickItem key={p.id} onClick={() => setLinkPlayerId(p.id)}>
                    {p.email} · {p.first_name} {p.last_name}
                  </PickItem>
                ))}
              {!searchHits?.length && <PickItem style={{ cursor: 'default', color: '#9ca3af' }}>No results — search</PickItem>}
            </PickList>
            <BtnPrimary
              type="button"
              disabled={linkMutation.isLoading || !linkTeamId || !linkPlayerId}
              onClick={() => linkMutation.mutate()}
            >
              {linkMutation.isLoading ? 'Linking…' : 'Link to roster'}
            </BtnPrimary>
          </Card>
        </>
      )}

      {tab === 'season' && (
        <Card>
          <CardTitle>Phase roster: 2 players + 1 team + phase registration</CardTitle>
          <Hint>
            Creates both player logins, one team shell, full roster (captain + partner), and a paid active phase
            registration for the selected phase. No Stripe. Place the team in a division afterward if needed.
          </Hint>

          <Grid>
            <Label>
              Season (optional — filters phases)
              <Select
                value={seasonRoster.campaignId}
                onChange={(e) =>
                  setSeasonRoster((s) => ({
                    ...s,
                    campaignId: e.target.value,
                    seasonId: '',
                  }))
                }
              >
                <option value="">All phases (no season filter)</option>
                {campaigns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title || c.name || c.id}
                  </option>
                ))}
              </Select>
            </Label>
            <Label>
              Phase *
              <Select
                value={seasonRoster.seasonId}
                onChange={(e) => setSeasonRoster((s) => ({ ...s, seasonId: e.target.value }))}
              >
                <option value="">Choose phase…</option>
                {seasonOptions.map((s) => {
                  // Linked campaign seasons use season_id; plain seasons use id
                  const sid = s.season_id || s.id;
                  const label = s.season_name || s.name || sid;
                  return (
                    <option key={sid} value={sid}>
                      {label}
                    </option>
                  );
                })}
              </Select>
            </Label>
            <Label>
              Team display name *
              <Input
                value={seasonRoster.team_name}
                onChange={(e) => setSeasonRoster((s) => ({ ...s, team_name: e.target.value }))}
              />
            </Label>
            <Label>
              Canton *
              <Select
                value={seasonRoster.canton}
                onChange={(e) => setSeasonRoster((s) => ({ ...s, canton: e.target.value }))}
              >
                <option value="">—</option>
                {cantons.map((c) => (
                  <option key={c.code || c.name || c} value={c.code || c.name || c}>
                    {c.name || c.code || c}
                  </option>
                ))}
              </Select>
            </Label>
            <Label>
              Team skill average (optional)
              <Input
                value={seasonRoster.team_skill_level}
                onChange={(e) => setSeasonRoster((s) => ({ ...s, team_skill_level: e.target.value }))}
              />
            </Label>
          </Grid>

          <CardTitle style={{ marginTop: '1.25rem' }}>Player 1 — captain</CardTitle>
          <Grid>
            <Label>
              Email *
              <Input
                type="email"
                value={seasonRoster.captain.email}
                onChange={(e) =>
                  setSeasonRoster((s) => ({
                    ...s,
                    captain: { ...s.captain, email: e.target.value },
                  }))
                }
                autoComplete="off"
              />
            </Label>
            <Label>
              Temp password * (min 8)
              <Input
                type="password"
                value={seasonRoster.captain.password}
                onChange={(e) =>
                  setSeasonRoster((s) => ({
                    ...s,
                    captain: { ...s.captain, password: e.target.value },
                  }))
                }
                autoComplete="new-password"
              />
            </Label>
            <Label>
              First name *
              <Input
                value={seasonRoster.captain.first_name}
                onChange={(e) =>
                  setSeasonRoster((s) => ({
                    ...s,
                    captain: { ...s.captain, first_name: e.target.value },
                  }))
                }
              />
            </Label>
            <Label>
              Last name *
              <Input
                value={seasonRoster.captain.last_name}
                onChange={(e) =>
                  setSeasonRoster((s) => ({
                    ...s,
                    captain: { ...s.captain, last_name: e.target.value },
                  }))
                }
              />
            </Label>
            <Label>
              Skill
              <Input
                value={seasonRoster.captain.skill_level}
                onChange={(e) =>
                  setSeasonRoster((s) => ({
                    ...s,
                    captain: { ...s.captain, skill_level: e.target.value },
                  }))
                }
              />
            </Label>
          </Grid>

          <CardTitle style={{ marginTop: '1.25rem' }}>Player 2 — partner</CardTitle>
          <Grid>
            <Label>
              Email *
              <Input
                type="email"
                value={seasonRoster.partner.email}
                onChange={(e) =>
                  setSeasonRoster((s) => ({
                    ...s,
                    partner: { ...s.partner, email: e.target.value },
                  }))
                }
                autoComplete="off"
              />
            </Label>
            <Label>
              Temp password * (min 8)
              <Input
                type="password"
                value={seasonRoster.partner.password}
                onChange={(e) =>
                  setSeasonRoster((s) => ({
                    ...s,
                    partner: { ...s.partner, password: e.target.value },
                  }))
                }
                autoComplete="new-password"
              />
            </Label>
            <Label>
              First name *
              <Input
                value={seasonRoster.partner.first_name}
                onChange={(e) =>
                  setSeasonRoster((s) => ({
                    ...s,
                    partner: { ...s.partner, first_name: e.target.value },
                  }))
                }
              />
            </Label>
            <Label>
              Last name *
              <Input
                value={seasonRoster.partner.last_name}
                onChange={(e) =>
                  setSeasonRoster((s) => ({
                    ...s,
                    partner: { ...s.partner, last_name: e.target.value },
                  }))
                }
              />
            </Label>
            <Label>
              Skill
              <Input
                value={seasonRoster.partner.skill_level}
                onChange={(e) =>
                  setSeasonRoster((s) => ({
                    ...s,
                    partner: { ...s.partner, skill_level: e.target.value },
                  }))
                }
              />
            </Label>
          </Grid>

          <BtnPrimary
            type="button"
            disabled={!seasonRosterReady || seasonRosterMutation.isLoading}
            onClick={() => {
              setSeasonRosterResult(null);
              seasonRosterMutation.mutate();
            }}
          >
            {seasonRosterMutation.isLoading ? 'Creating…' : 'Create phase roster'}
          </BtnPrimary>

          {seasonRosterResult && (
            <div
              style={{
                marginTop: '1rem',
                padding: '0.85rem 1rem',
                background: '#ecfdf5',
                border: '1px solid #a7f3d0',
                borderRadius: 8,
                fontSize: '0.85rem',
                color: '#065f46',
                lineHeight: 1.5,
              }}
            >
              <strong>Created</strong>
              <div>
                Phase: {seasonRosterResult.season?.name} ({String(seasonRosterResult.season?.id || '').slice(0, 8)}
                …)
              </div>
              <div>
                Team: {seasonRosterResult.team?.team_name} ({String(seasonRosterResult.team?.id || '').slice(0, 8)}
                …) · {seasonRosterResult.team?.canton}
              </div>
              <div>
                Captain: {seasonRosterResult.player_captain?.email} — use player login
              </div>
              <div>
                Partner: {seasonRosterResult.player_partner?.email} — use player login
              </div>
              <div style={{ marginTop: '0.35rem', color: '#047857' }}>
                Paid &amp; active for this phase. Place in a division via League Management if needed.
              </div>
            </div>
          )}
        </Card>
      )}
    </Wrap>
  );
};

export default AdminTeamProvisioningPanel;
